import {cmp} from 'lib/utilities/functions';

export default function DetectBreakpoints(selector = '.breakpoint') {
    let windowWidth = window.innerWidth;
    let current = null;
    let breakpoints = [];
    let map = {};
    let listeners = [];

    function constructor() {
        update_breakpoints();
        update_current_breakpoint();
        add_events();
    }

    function add_events() {
        window.addEventListener('resize', on_resize);
        window.addEventListener('load', on_resize);
    }

    function remove_events() {
        window.removeEventListener('resize', on_resize);
        window.removeEventListener('load', on_resize);
    }

    function on_resize() {
        // Only update current breakpoint on change of device width, to save a re-evaluation everytime you scroll up
        // and down on mobile as the browser URL bar hides and shows, triggering a resize event.
        if (windowWidth !== window.innerWidth) {
            windowWidth = window.innerWidth;

            update_current_breakpoint();
        }
    }

    function find_breakpoint_elements() {
        return Array.from(document.querySelectorAll(selector));
    }

    function element_to_breakpoint(element, index) {
        const name = element.getAttribute('data-name');

        if (name === null) {
            throw new Error('Missing data-name for breakpoint element');
        }

        return {
            element: element,
            name: name,
            index: index
        }
    }

    function create_map(breakpoints) {
        const map = {};

        for (const breakpoint of breakpoints) {
            map[breakpoint.name] = breakpoint;
        }

        return map;
    }

    function find_breakpoints() {
        return find_breakpoint_elements()
            .map(element_to_breakpoint);
    }

    function update_breakpoints() {
        breakpoints = find_breakpoints();
        map = create_map(breakpoints);
    }

    function listen(listener) {
        listeners.push(listener);
    }

    function stop_listening(listener) {
        const index = listeners.indexOf(listener);

        if (index > -1) {
            listeners.splice(index, 1);
        }
    }

    function call_listener(listener) {
        listener(current);
    }

    function call_listeners() {
        listeners.forEach(call_listener);
    }

    function set_current_breakpoint(breakpoint) {
        current = breakpoint;

        call_listeners();
    }

    function update_current_breakpoint() {
        const breakpoint = find_current_breakpoint();

        if (breakpoint !== current) {
            set_current_breakpoint(breakpoint);
        }
    }

    function is_current_breakpoint(breakpoint) {
        const style = getComputedStyle(breakpoint.element);

        return style.display !== 'none';
    }

    function find_current_breakpoint() {
        for (const breakpoint of breakpoints) {
            if (is_current_breakpoint(breakpoint)) {
                return breakpoint;
            }
        }
    }

    function get_current() {
        return current;
    }

    function resolve_breakpoints() {
        if (breakpoints.length === 0) {
            update_breakpoints();
            update_current_breakpoint();
        }
    }

    function resolve_breakpoint(breakpoint) {
        resolve_breakpoints();

        if (typeof breakpoint === 'object') {
            return breakpoint.name;
        }

        if (breakpoint in map) {
            return map[breakpoint];
        }

        throw new Error('Invalid breakpoint: ' + breakpoint);
    }

    function breakpoint_cmp(nameA, nameB = null, comparator = null) {
        // Switch the logic so that we are comparing the given breakpoint to the current breakpoint in the same direction
        // as is implied by usage.
        if (nameB === null) {
            nameB = nameA;
            nameA = current.name;
        }

        // Get current first to ensure initialised
        const a = resolve_breakpoint(nameA);
        const b = resolve_breakpoint(nameB);

        if (comparator) {
            return comparator(a.index, b.index);
        }

        return cmp(a.index, b.index);
    }

    function gt(nameA, nameB = null) {
        return breakpoint_cmp(nameA, nameB, function (a, b) {
            return a > b;
        })
    }

    function gte(nameA, nameB = null) {
        return breakpoint_cmp(nameA, nameB, function (a, b) {
            return a >= b;
        })
    }

    function lt(nameA, nameB = null) {
        return breakpoint_cmp(nameA, nameB, function (a, b) {
            return a < b;
        })
    }

    function lte(nameA, nameB = null) {
        return breakpoint_cmp(nameA, nameB, function (a, b) {
            return a <= b;
        })
    }

    function eq(nameA, nameB = null) {
        return breakpoint_cmp(nameA, nameB, function (a, b) {
            return a === b;
        })
    }

    function ne(nameA, nameB = null) {
        return breakpoint_cmp(nameA, nameB, function (a, b) {
            return a !== b;
        })
    }

    function get_breakpoints() {
        resolve_breakpoints();

        return breakpoints;
    }

    function destroy() {
        remove_events();
        current = null;
        listeners = [];
        breakpoints = [];
        map = {};
    }

    constructor();

    return {
        destroy,
        listen,
        stop_listening,
        get_breakpoints,
        get_current,
        cmp: breakpoint_cmp,
        gt,
        gte,
        lt,
        lte,
        eq,
        ne,
        is: eq
    }
}